import type { IntrinsicElementWrapperComponent, OverrideComponentProps } from '@cometjs/react-utils';
import * as React from 'react';
import { rem } from 'polished';
import { cx } from '@emotion/css';
import FadeLoader from 'react-spinners/FadeLoader';

import { styled } from './themeContext';
import containerSvgUrl from './mainButton/container.svg';
import edgeSvgUrl from './mainButton/edge.svg';
import greenDecoSvgUrl from './mainButton/greenDeco.svg';
import blueDecoSvgUrl from './mainButton/blueDeco.svg';
import Link from './Link';

type MainButtonProps = {
  children: string,
  deco?: boolean,
  state?: 'default' | 'loading' | 'disabled',
};

type MainButtonComponent = IntrinsicElementWrapperComponent<'button', MainButtonProps>;
type MainButtonLinkComponent = React.FC<Omit<React.ComponentProps<typeof Link>,
  keyof MainButtonProps> & MainButtonProps>;

const ButtonContainer = styled.button((props) => ({
  'cursor': 'pointer',
  'display': 'inline-flex',
  'alignItems': 'center',
  'border': 'none',
  'padding': `${rem(14)} ${rem(4)}`,
  'boxSizing': 'border-box',
  'margin': `0 ${rem(29)}`,
  'whiteSpace': 'nowrap',
  'textDecoration': 'none',

  // size
  'fontSize': rem(20),
  'fontWeight': 'bold',
  'color': '#362310',
  'height': rem(64),
  ':lang(ja), :lang(zh-Hant), :lang(en)': {
    fontSize: rem(15),
  },
  ':lang(ko)': {
    fontSize: rem(22),
    height: rem(70),
  },
  [props.theme.media['md']]: {
    'fontSize': rem(38),
    'margin': `0 ${rem(64)}`,
    'padding': `${rem(28)} ${rem(15)}`,
    'height': rem(112),
    ':lang(ja), :lang(zh-Hant), :lang(en)': {
      fontSize: rem(30),
    },
    ':lang(ko)': {
      fontSize: rem(38),
      height: rem(112),
    },
  },

  // background svg
  'position': 'relative',
  'backgroundColor': 'transparent',
  'backgroundImage': `url(${containerSvgUrl})`,
  'backgroundRepeat': 'repeat no-repeat',
  'backgroundSize': 'auto 100%',
  '&:disabled': {
    opacity: '0.7',
    filter: 'saturate(50%)',
  },
  '::before, ::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    width: rem(45),
    height: '100%',
    backgroundImage: `url(${edgeSvgUrl})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'right center',
    [props.theme.media['md']]: {
      width: rem(64),
    },
  },
  '::before': {
    right: '100%',
    marginRight: rem(-1),
  },
  '::after': {
    left: '100%',
    marginLeft: rem(-1),
    transform: 'matrix(-1, 0, 0, 1, 0, 0)',
  },
}));

const FadeLoaderWrapper = styled.div({
  position: 'absolute',
  left: '50%',
  top: '50%',
  transform: 'translateX(-50%) translateY(-50%)',
});

const LinkContainer = ButtonContainer.withComponent(Link);

const TextContainer = styled.span({
  'position': 'relative',
  'margin': '0 0.2em',
  '&.deco': {
    'margin': '0 1.2em',

    '::before, ::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '0.6em',
      height: '0.6em',
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'contain',
      backgroundPosition: 'center',
    },
    '::before': {
      right: '100%',
      marginRight: rem(12),
      backgroundImage: `url(${greenDecoSvgUrl})`,
    },
    '::after': {
      left: '100%',
      marginLeft: rem(12),
      backgroundImage: `url(${blueDecoSvgUrl})`,
    },
  },
});

const Text = styled.span({
  '&.loading': {
    opacity: 0,
  },
});

const MainButton: MainButtonComponent = (
  { children, deco = true, state = 'initial', ...props },
  forwardedRef,
) => {
  return (
    <ButtonContainer ref={forwardedRef} {...props} disabled={state === 'disabled'} className={`${cx(deco && 'deco')} ${state}`}>
      <TextContainer className={cx(deco && 'deco')}>
        {state === 'loading' &&
          <FadeLoaderWrapper>
            <FadeLoader color='#362310' />
          </FadeLoaderWrapper>
        }
        <Text className={state}>{children}</Text>
      </TextContainer>
    </ButtonContainer>
  );
};

export default React.forwardRef(MainButton);

export const MainButtonLink: MainButtonLinkComponent = (
  { children, deco = true, state = 'initial', ...props },
) => {
  return (
    <LinkContainer {...props} className={`${cx(deco && 'deco')} ${state}`}>
      <TextContainer className={cx(deco && 'deco')}>
        {state === 'loading' &&
          <FadeLoaderWrapper>
            <FadeLoader color='#362310' />
          </FadeLoaderWrapper>
        }
        <Text className={state}>{children}</Text>
      </TextContainer>
    </LinkContainer>
  );
};
